/**
 * Scene names (Tom, 2026-11-02: "we should name the scenes rather than just
 * number them / Scene 8 - Ordering Food"). The names are NOT held here: Popty
 * is their one source (services/shared/pod-scene-names) and writes each
 * course's names, in that course's KNOWN language, to
 * `listening_pods.metadata.scene_names` ({"1": "...", ...}). This module only
 * reads that key and composes the header.
 */
import type { SupabaseClient } from '@supabase/supabase-js'

/** Scene number (as a string key) → name in the learner's known language. */
export type SceneNames = Record<string, string>

/** Keep only non-blank string names; undefined when nothing usable is left. */
export function cleanSceneNames(raw: unknown): SceneNames | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const out: SceneNames = {}
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'string' && v.trim()) out[k] = v.trim()
  }
  return Object.keys(out).length ? out : undefined
}

/**
 * "Scene 8 - Ordering Drinks", or "Scene 8" alone when the scene has no name —
 * never a dangling dash. `label` renders the localised "Scene {n}".
 */
export function sceneHeader(
  sceneNumber: number,
  names: SceneNames | undefined,
  label: (n: number) => string,
): string {
  const name = names?.[String(sceneNumber)]?.trim()
  return name ? `${label(sceneNumber)} - ${name}` : label(sceneNumber)
}

/**
 * Names for every listed pod, in ONE read. Never throws: a failed read costs
 * the names (the header falls back to "Scene N"), never the scenes.
 */
export async function fetchSceneNames(
  client: SupabaseClient,
  podIds: string[],
): Promise<Map<string, SceneNames>> {
  const out = new Map<string, SceneNames>()
  if (!podIds.length) return out
  try {
    const { data, error } = await client
      .from('listening_pods')
      .select('id, scene_names:metadata->scene_names')
      .in('id', podIds)
    if (error) return out
    for (const row of (data || []) as Array<{ id: string; scene_names?: unknown }>) {
      const names = cleanSceneNames(row.scene_names)
      if (names) out.set(row.id, names)
    }
  } catch {
    /* names are a nicety; the scene list stands without them */
  }
  return out
}
